import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useMemo } from "react";
import { Image } from "react-native";
import { useFragment } from "react-relay";

import { useRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { useFlags } from "@/utils/launchDarkly";
import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import type { ConfirmedBookingModalType } from "../BookingSummary/atoms/stores";
import { BookingConfirmedModalShareSection } from "./BookingConfirmedModalShareSection";
import {
  BookingConfirmedModal_Booking$key,
  ChargePaymentStatus,
} from "./__generated__/BookingConfirmedModal_Booking.graphql";

const BOOKING_CONFIRMED_IMAGE_URL = "confirmed-graphic_o6w3mm";
const REQUEST_BOOKING_CONFIRMED_IMAGE_URL = "request-recieved_cpjorp";
const CONFIRMED_IMAGE_SIZE = 72;
const CONFIRMED_IMAGE_RADIUS = CONFIRMED_IMAGE_SIZE / 2;

type BookingConfirmedModalProps = {
  isOpen: boolean;
  setConfirmedBookingModal: (open: boolean) => void;
  bookingRef: BookingConfirmedModal_Booking$key;
  type?: ConfirmedBookingModalType;
};

const acceptableChargeStates: ChargePaymentStatus[] = ["UNPAID", "NO_CHARGES"];

export const BookingConfirmedModal = ({
  isOpen,
  setConfirmedBookingModal,
  bookingRef,
  type = "default",
}: BookingConfirmedModalProps) => {
  const booking = useFragment(BookingConfirmedModalFragmentString, bookingRef);
  const date = booking?.startSlot?.agenda?.date;
  const facilitySlug = booking?.startSlot?.agenda?.facility?.slug;
  const isRequestBooking = booking?.startSlot?.type === "QUEUE";
  const { top: safeAreaTop } = useRealSafeAreaInsets();

  const isLessonBooking = booking?.__typename === "LessonBooking";

  const { patronJoinBookingFlow, inviteMemberFlow } = useFlags();

  const clubSupportsPrivateBookings =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;

  const hasUnknownPlayers = booking?.rounds?.edges?.some(
    (edge) => !edge?.node?.player?.id
  );
  const hasMemberHoldSlots = booking?.rounds?.edges?.some(
    (edge) =>
      edge?.node?.__typename === "MemberRound" && !edge?.node?.player?.id
  );
  const hasAvailableCapacity = (booking?.startSlot?.availableCapacity ?? 0) > 0;
  const requiresPlayerCounts = booking?.startSlot?.requirePlayerCounts;

  const isDurationFacility =
    booking?.startSlot?.agenda?.facility?.type !== "GOLF_COURSE";

  const isNotYetPaid =
    !!acceptableChargeStates.includes(booking?.paymentStatus ?? "PAID") &&
    !!booking?.rounds?.edges?.every(
      (edge) =>
        edge?.node?.__typename === "CustomerRound" &&
        acceptableChargeStates.includes(edge?.node?.paymentStatus ?? "PAID")
    );

  const hasSlotsToInvite =
    hasUnknownPlayers ||
    (hasAvailableCapacity &&
      isNotYetPaid &&
      isDurationFacility &&
      !requiresPlayerCounts);

  const shouldShowPublicInvite =
    patronJoinBookingFlow && hasSlotsToInvite && !clubSupportsPrivateBookings;

  const shouldShowPrivateInvite =
    inviteMemberFlow && hasMemberHoldSlots && !!clubSupportsPrivateBookings;

  const shouldShowInviteBox =
    booking?.__typename !== "LessonBooking" &&
    (shouldShowPublicInvite || shouldShowPrivateInvite);

  const bookingConfirmedImageUrl = getThumbnail(
    BOOKING_CONFIRMED_IMAGE_URL,
    72,
    72
  );

  const requestBookingConfirmedImageUrl = getThumbnail(
    REQUEST_BOOKING_CONFIRMED_IMAGE_URL,
    72,
    72
  );

  const dateString = useMemo(() => {
    const parsedDate = date ? parse(date, "yyyy-MM-dd", new Date()) : null;
    return parsedDate ? format(parsedDate, "MMM do") : "soon";
  }, [date]);

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  return (
    <StandardModal
      hideCloseButton
      allowShrink
      isOpen={isOpen}
      onClose={() => {
        setConfirmedBookingModal(false);
      }}
      backgroundColorOverride="surface-secondary"
      content={
        <VStack
          flex={1}
          height="100%"
          p={6}
          alignItems="center"
          gap={4}
          backgroundColor={"surface-secondary"}
          style={{ paddingTop: safeAreaTop + 48 }}
          testID="booking-confirmed-modal"
        >
          <Box position={"absolute"} top={safeAreaTop + 16} left={16}>
            <Button
              variant="secondary"
              accessibilityLabel={t`Close Booking Confirmed Modal Button`}
              testID="booking-confirmed-modal-close-button"
              size="small"
              onPress={() => {
                setConfirmedBookingModal(false);
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Box
            shadowColor="icon-primary"
            shadowOpacity={0.3}
            shadowOffset={{ width: 0, height: 16 }}
            shadowRadius={48}
            style={{
              borderRadius: CONFIRMED_IMAGE_RADIUS,
              overflow: "hidden",
            }}
          >
            <Image
              accessibilityLabel={
                isRequestBooking
                  ? t`Booking request received`
                  : t`Booking confirmed`
              }
              source={{
                uri: isRequestBooking
                  ? requestBookingConfirmedImageUrl?.toURL()
                  : bookingConfirmedImageUrl?.toURL(),
              }}
              style={{
                borderRadius: CONFIRMED_IMAGE_RADIUS,
                width: CONFIRMED_IMAGE_SIZE,
                height: CONFIRMED_IMAGE_SIZE,
              }}
              resizeMode="contain"
            />
          </Box>
          <VStack gap={2} alignItems="center">
            <Text variant="mainTitle3">
              {type === "recurring-request-updated"
                ? t`Your recurring request is updated`
                : type === "recurring-request-received"
                ? t`Your recurring request is received`
                : isRequestBooking
                ? t`Your request is received`
                : isLessonBooking
                ? t`Your lesson is confirmed`
                : t`Your booking is confirmed`}
            </Text>
            <Text variant="mainSmallMedium">
              {isRequestBooking
                ? t`We’ll notify you as soon as a time
                becomes available!`
                : t`See you ${dateString}!`}
            </Text>
          </VStack>
          <HStack gap={4} width="100%" padding={2}>
            <Button
              style={{ flex: 1 }}
              variant="outline"
              size="small"
              accessibilityLabel={t`Book Again Button`}
              testID="booking-confirmed-modal-book-again-button"
              onPress={() => {
                setConfirmedBookingModal(false);
                if (isLessonBooking) {
                  navigation.navigate("Lessons", {
                    facilitySlug: facilitySlug ?? "",
                    screen: "LessonsHome",
                  });
                } else {
                  navigation.navigate("Agenda", {
                    facilitySlug: facilitySlug ?? "",
                    date: date,
                  });
                }
              }}
            >
              {t`Book Again`}
            </Button>
            <Button
              style={{ flex: 1 }}
              variant="main"
              size="small"
              accessibilityLabel={t`Go Back Home Button`}
              testID="booking-confirmed-modal-go-back-home-button"
              onPress={() => {
                setConfirmedBookingModal(false);
                navigation.navigate("Home", { screen: "MainHome" });
              }}
            >
              {t`Go Back Home`}
            </Button>
          </HStack>

          {shouldShowInviteBox ? (
            <BookingConfirmedModalShareSection bookingRef={booking ?? null} />
          ) : null}
        </VStack>
      }
    />
  );
};

const BookingConfirmedModalFragmentString = graphql`
  fragment BookingConfirmedModal_Booking on Booking {
    id
    startSlot {
      type
      ... on TimeSlot {
        availableCapacity(bookingType: STANDARD)
        requirePlayerCounts
      }
      agenda {
        date
        facility {
          slug
          type
          club {
            slug
            supportsPrivate
          }
        }
      }
    }
    __typename
    ... on StandardBooking {
      status
      paymentStatus
      rounds {
        edges {
          node {
            __typename
            player {
              id
            }
            ... on CustomerRound {
              id
              paymentStatus
            }
          }
        }
      }
    }
    ...BookingConfirmedModalShareSection_Booking
  }
`;
